import moment from "moment";

export type UsageValuePresetId =
    | "d30"
    | "d90"
    | "qtd"
    | "lq"
    | "ytd"
    | "custom";

export const usageValuePresets: { id: UsageValuePresetId; label: string }[] = [
    { id: "d30", label: "Last 30 days" },
    { id: "d90", label: "Last 90 days" },
    { id: "qtd", label: "Quarter to date" },
    { id: "lq", label: "Last quarter" },
    { id: "ytd", label: "Year to date" },
    { id: "custom", label: "Custom…" },
];

/** A day-aligned [start, end) range; end is exclusive so repeat requests share a cache key. */
export type UsageValueRange = { start: moment.Moment; end: moment.Moment };

export const rangeForPreset = (
    preset: Exclude<UsageValuePresetId, "custom">,
    now: moment.Moment = moment(),
): UsageValueRange => {
    const tomorrow = now.clone().startOf("day").add(1, "day");
    switch (preset) {
        case "d30":
            return {
                start: tomorrow.clone().subtract(30, "days"),
                end: tomorrow,
            };
        case "d90":
            return {
                start: tomorrow.clone().subtract(90, "days"),
                end: tomorrow,
            };
        case "qtd":
            return { start: now.clone().startOf("quarter"), end: tomorrow };
        case "lq": {
            const thisQuarter = now.clone().startOf("quarter");
            return {
                start: thisQuarter.clone().subtract(1, "quarter"),
                end: thisQuarter,
            };
        }
        case "ytd":
            return { start: now.clone().startOf("year"), end: tomorrow };
    }
};

/** "Jul 18 – Oct 15, 2026", showing the inclusive last day. */
export const formatRangeLabel = ({ start, end }: UsageValueRange) => {
    const last = end.clone().subtract(1, "day");
    const startFormat = start.year() === last.year() ? "MMM D" : "MMM D, YYYY";
    return `${start.format(startFormat)} – ${last.format("MMM D, YYYY")}`;
};

const BUSINESS_DAY_HOURS = 8;

export const formatNumber = (value: number) =>
    Math.round(value).toLocaleString("en-US");

export const formatHours = (ms: number) => {
    const hours = ms / 3_600_000;
    return `${hours >= 100 ? formatNumber(hours) : hours.toFixed(1)} h`;
};

export const formatBusinessDays = (ms: number) => {
    const days = (ms / 3_600_000 / BUSINESS_DAY_HOURS).toFixed(1);
    return `${days} ${days === "1.0" ? "business day" : "business days"}`;
};

export const formatDate = (value?: string | null) =>
    value ? moment(value).format("MMM D, YYYY") : "—";

export const formatDateTime = (value?: string | null) =>
    value ? moment(value).format("MMM D, YYYY h:mm A") : "—";

/**
 * Change versus the previous period as a whole percentage, or null when there is no baseline to
 * compare against.
 */
export const percentChange = (current: number, previous: number) =>
    previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;

// Kept in step with AuthentizenEmailDomains in UsageValueController.
const AUTHENTIZEN_EMAIL_DOMAINS = [
    "@authenticx.com",
    "@beauthenticx.com",
    "@authcx.com",
];

/** Authentizens are Authenticx employees with accounts in the tenant. */
export const isAuthentizenEmail = (email?: string | null) => {
    const normalized = email?.trim().toLowerCase();
    return (
        !!normalized &&
        AUTHENTIZEN_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain))
    );
};

/** "1 user", "3 users". */
export const pluralize = (count: number, noun: string) =>
    `${formatNumber(count)} ${noun}${count === 1 ? "" : "s"}`;
